import { observable, action } from 'mobx';
import _ from 'lodash';

import {
  Bloodwork,
  BloodworkRange,
  RangeOverride,
  applyRangeOverrides,
  rangeOverridesSignature,
  staleRangeOverrides,
} from '../models';
import { BloodworkStore } from './bloodwork-store';
import { LoadingStore } from './loading-store';
import { UserProfileUIStore } from './user-profile-ui-store';
import { CustomMarker, CustomMarkerDefinition } from '../models/CustomMarker';
import { placeRowsOntoTest } from '../models/ImportedExtrasPlacement';

export class BloodworkEntryStore {
  static DEFAULT_LIMIT: number = 5;
  
  @observable bloodworks: Bloodwork[] = [];
  @observable clientBloodworks: Bloodwork[] = [];
  @observable bloodworkRanges: BloodworkRange[] = [];
  /**
   * The same table, row for row as stored, before init() derived anything.
   *
   * `bloodworkRanges` above is what every analysis reads and it is built FROM
   * this: bloodworkRangesFrom() runs init() over each row, which fills in every
   * optimal and alarm bound the row does not carry and writes the result onto
   * the row. That is the right shape for an analysis and the wrong one for
   * anything that needs to know what LabSmarts actually authored versus what
   * arithmetic filled in, or that needs to put a practitioner's own numbers
   * underneath that arithmetic. My Ranges is both, so the stored rows are kept
   * here and never initialized.
   */
  @observable storedBloodworkRanges: BloodworkRange[] = [];
  @observable currentBloodwork: Bloodwork = new Bloodwork();
  @observable hideEnterTestResults: boolean = false;

  /**
   * The test the results list is analyzing, and the older test it is being
   * compared against.
   *
   * This is BloodworkDisplay's own selection and it used to live in that
   * component's state. It sits here because the entry form is its SIBLING
   * rather than its parent (BloodworkEntryContainer renders both one above the
   * other), and saving a test has to be able to reach it. An analysis opened
   * before a save describes a DIFFERENT test, and the panel header says only
   * "Test Results Analysis" with no client or date on it, so left on screen it
   * reads as the analysis of the test just saved.
   *
   * BloodworkDisplay clears it on unmount, so this stays a within-page
   * selection. Carrying it across a route change would only move the same
   * confusion somewhere else: an analysis opened on the dashboard would follow
   * the practitioner onto the entry page and sit below a form for someone else.
   */
  @observable analyzedBloodwork?: Bloodwork = undefined;
  @observable analyzedPreviousBloodwork?: Bloodwork = undefined;

  constructor(
    private bloodworkStore: BloodworkStore, 
    private loadingStore: LoadingStore,
    private userProfileUIStore: UserProfileUIStore) {
  }

  @action refreshBloodworks = (): Promise<any> => {
    console.log('***************** refreshBloodworks **********************************');
    this.bloodworks = [];
    return this.loadBloodworks(BloodworkEntryStore.DEFAULT_LIMIT);
  }

  @action loadClientBloodworks = async (clientId: string): Promise<Bloodwork[]> => {
    this.loadingStore.appIsBusy();
    
    const clientBloodworks = await this.bloodworkStore.load(clientId);
    this.clientBloodworks = _.orderBy(clientBloodworks, ['collectionDate', 'createDate'], ['desc', 'desc']);
    
    this.loadingStore.appIsNotBusy();
    
    return this.clientBloodworks;
  }

  @action resetClientBloodworks(): void {
    this.clientBloodworks = [];
  }

  @action setHideEnterTestResults(hideEnterTestResults: boolean): void {
    this.hideEnterTestResults = hideEnterTestResults;
  }

  /**
   * Both together, always: a comparison is meaningless once the test it was
   * comparing has gone, and clearing is the common case (pass nothing).
   */
  @action setAnalyzedBloodwork(
    bloodwork?: Bloodwork,
    previousBloodwork?: Bloodwork,
  ): void {
    this.analyzedBloodwork = bloodwork;
    this.analyzedPreviousBloodwork = previousBloodwork;
  }

  @action loadBloodworks = (limit: number, lastCreateDate?: string): Promise<any> => {
    console.log('***************** loadBloodworks **********************************');
    this.loadingStore.appIsBusy();
    return this.bloodworkStore.load(undefined, limit, lastCreateDate).then((data) => {
      this.bloodworks.push(...data.bloodworks);
      this.loadingStore.appIsNotBusy();
      return data;
    }, (error: any) => {
      console.error(error);
      this.loadingStore.appIsNotBusy();
      return {};
    });
  }

  @action setCurrentBloodwork = (bloodwork: Bloodwork): void => {
    this.currentBloodwork = bloodwork;
  }

  @action resetCurrentBloodwork = (): Bloodwork => {
    this.currentBloodwork = new Bloodwork();
    return this.currentBloodwork;
  }

  // Mirrors FurtherConsiderationsEntryStore.loadFurtherConsiderations: the
  // BloodworkRange table is identical for every practitioner, read-only from
  // the app, and ~650KB of JSON, but four containers refetched it on every
  // mount. One shared promise per session; failures are not cached so the next
  // mount still retries.
  private rangesPromise?: Promise<BloodworkRange[]>;

  /**
   * The range table every analysis is built on: LabSmarts' rows with this
   * practitioner's My Ranges numbers applied.
   *
   * Resolves `effectiveRanges` rather than the raw set, which is what points
   * all five `new BloodworkAnalysis` call sites — the analysis screen, the
   * PDF, the comparison, the details modal and the AI export — at the
   * practitioner's ranges in one move. The wrapper is a `.then()` on the
   * cached fetch, not a second download, and it is re-read per call on
   * purpose: the promise resolved once, but an override saved since then has
   * to reach the next report that opens.
   */
  loadBloodworkRanges(): Promise<BloodworkRange[]> {
    return Promise.all([
      this.loadStoredBloodworkRanges(),
      // The overrides live on the profile, so a caller that builds its
      // analysis the moment the table lands would grade on LabSmarts' numbers
      // whenever the profile had not arrived yet. The PDF window is exactly
      // that caller, and the result would be a PDF whose bands disagreed with
      // the screen it was exported from.
      this.profileForRanges(),
    ]).then(() => this.effectiveRanges);
  }

  /** The profile, loaded if it is not here yet. Never fails the range load. */
  private profileForRanges(): Promise<any> {
    const store = this.userProfileUIStore;
    if (!store || store.currentUserProfile) {
      return Promise.resolve(store ? store.currentUserProfile : undefined);
    }
    return store.loadCurrentUserProfile().then(
      profile => profile,
      (error: any) => {
        // A profile that will not load is already reported by the pages that
        // need it. Here it means no overrides, which is LabSmarts' table —
        // the right thing to fall back to.
        console.error(error);
        return undefined;
      },
    );
  }

  /**
   * LabSmarts' table as stored, fetched once per session.
   *
   * The My Ranges page reads the stored rows through `storedBloodworkRanges`;
   * everything that grades a result wants `loadBloodworkRanges()` above.
   */
  loadStoredBloodworkRanges(): Promise<BloodworkRange[]> {
    if (this.rangesPromise) {
      return this.rangesPromise;
    }

    this.loadingStore.appIsBusy();
    this.rangesPromise = this.bloodworkStore.loadBloodworkRanges().then((storedRanges) => {
      // Initialized HERE rather than in the fetch, so the stored rows survive.
      // See storedBloodworkRanges.
      const bloodworkRanges = BloodworkRange.bloodworkRangesFrom(storedRanges);
      this.storedBloodworkRanges = storedRanges;
      this.bloodworkRanges = bloodworkRanges;
      this.loadingStore.appIsNotBusy();
      return bloodworkRanges;
    }, (error: any) => {
      console.error(error);
      this.rangesPromise = undefined;
      this.loadingStore.appIsNotBusy();
      return [];
    });
    return this.rangesPromise;
  }

  /**
   * Memo for effectiveRanges. Applying overrides means re-running init() over
   * 3,300 rows, and loadBloodworkRanges() is called on every report open, so
   * the result is kept until its inputs actually change — the stored array's
   * identity, its length, and the overrides themselves.
   *
   * Not a @computed: the profile is a class instance, so MobX does not track
   * writes to its fields and a computed would buy no reactivity here. What it
   * would add is a recompute on every read from outside a reaction, which is
   * exactly the cost this cache exists to avoid. Saving on the My Ranges page
   * REPLACES currentUserProfile, so the signature changes and the next report
   * opened is graded on the new numbers.
   */
  private effectiveRangesCache?: {
    rows: BloodworkRange[];
    count: number;
    signature: string;
    result: BloodworkRange[];
  };

  /** This practitioner's My Ranges numbers, entitlement applied. */
  get activeRangeOverrides(): RangeOverride[] {
    const profile = this.userProfileUIStore
      ? this.userProfileUIStore.currentUserProfile
      : undefined;
    if (!profile || !profile.activeRangeOverrides) {
      return [];
    }
    return profile.activeRangeOverrides();
  }

  /**
   * LabSmarts' table with this practitioner's own ranges written over it.
   *
   * Overrides go on the STORED rows and bloodworkRangesFrom() runs
   * afterwards, which is load-bearing: init() derives optimal, alarm, mean and
   * stdDev from whatever is present, so the arithmetic is built around the
   * practitioner's numbers instead of the override having to undo a
   * derivation. It also means the WBC percent-differential row expansion
   * inside bloodworkRangesFrom() cannot be bypassed by an override.
   */
  get effectiveRanges(): BloodworkRange[] {
    const overrides = this.activeRangeOverrides;
    const rows = this.storedBloodworkRanges || [];
    if (overrides.length === 0) {
      // Nothing to apply, so hand back the set the fetch already built rather
      // than running init() over the table a second time for no change.
      return this.bloodworkRanges;
    }
    const signature = rangeOverridesSignature(overrides);
    const cache = this.effectiveRangesCache;
    if (
      cache &&
      cache.rows === rows &&
      cache.count === rows.length &&
      cache.signature === signature
    ) {
      return cache.result;
    }
    const result = BloodworkRange.bloodworkRangesFrom(
      applyRangeOverrides(rows, overrides),
    );
    this.effectiveRangesCache = {
      rows,
      count: rows.length,
      signature,
      result,
    };
    return result;
  }

  /**
   * Overrides whose population no longer exists in the table — a row that was
   * re-banded or removed under them.
   *
   * Reported on the My Ranges page so the practitioner can re-enter them. A
   * stale override that vanishes with no notice is the same bug as one applied
   * to the wrong row.
   */
  get staleRangeOverrides(): RangeOverride[] {
    return staleRangeOverrides(
      this.storedBloodworkRanges || [],
      this.activeRangeOverrides,
    );
  }

  /**
   * Put a freshly saved test back over the copy these lists are holding.
   *
   * A test can exist twice in memory: the row in `bloodworks` /
   * `clientBloodworks`, and the copy BloodworkAnalysis makes of it
   * (`new Bloodwork(...)` in its constructor). Everything saved from the
   * analysis screen — section notes, supplement panel overrides, the client
   * list — is written to that copy, and nothing used to bring the result back
   * here. Manage Test Results' Edit button hands the entry form the row from
   * these lists, and the entry form re-posts whatever it was given.
   *
   * Notes happen to survive that: BloodworkNotes' constructor assigns the same
   * `notes` array rather than copying it, so the copy shares the note graph and
   * every in-place note write lands on the row too. Nothing else does. A field
   * the analysis screen ASSIGNS (`bloodwork.supplementEdits = [...]`) never
   * reaches the row at all, and the first such edit on a test is lost the next
   * time that test is edited — there is no shared array to mutate.
   *
   * So rather than leaning on that accident, the save closes the loop: one
   * test, one current copy. The record written back is the one the API echoed,
   * which is the item it stored.
   */
  @action private refreshSavedBloodwork = (saved: Bloodwork): void => {
    if (!saved || !saved.id) {
      return;
    }
    const replaceIn = (list: Bloodwork[]) => {
      const index = (list || []).findIndex(
        bloodwork => bloodwork && bloodwork.id === saved.id,
      );
      if (index !== -1) {
        list[index] = saved;
      }
    };
    replaceIn(this.bloodworks);
    replaceIn(this.clientBloodworks);
    // `currentBloodwork` is deliberately left alone. It is the entry form's own
    // record, set from these lists when Edit is clicked, and swapping it
    // underneath an open form would re-seed every field the practitioner is
    // part-way through typing.
  }

  /**
   * `silent` keeps the global spinner modal down for a background autosave.
   *
   * That spinner is a Bootstrap Modal, and while it is up the body carries
   * `modal-open` (overflow: hidden), which collapses the scrollable height and
   * pins the page at the top — the position is NOT restored when the modal
   * closes. For a save the practitioner asked for that costs a scroll the
   * caller can put back (see scrollPanelIntoView), but a save that fires by
   * itself while they are working must not move the page at all: the
   * Recommended Supplements card autosaves 800ms after a tick or a picked
   * product, and the practitioner is still reading the row they just clicked.
   *
   * A silent save touches neither appIsBusy nor appIsNotBusy: clearing the flag
   * would hide a spinner a concurrent user-initiated save had legitimately
   * raised. Silent saves report their own state instead — the card carries a
   * Saving / Changes saved line beside the button that caused them.
   */
  /**
   * Add the markers a lab delivered but nothing could hold, and fill their values.
   *
   * The one button behind imported-extras-notice.tsx. It owns BOTH writes
   * because they are one promise to the practitioner: the markers are in their
   * library and the numbers are on the chart. Splitting them across two callers
   * is how you get a library with a marker in it and a test that still shows
   * nothing.
   *
   * ORDER MATTERS. The profile is saved first, because the definitions are what
   * the placement reads: a test saved against definitions that failed to
   * persist would carry values whose ranges live nowhere, and a custom marker
   * with no range draws no bar and grades nothing. If the profile write throws,
   * the test is left exactly as it was and the notice still says what is
   * waiting, which is a state the practitioner can retry out of.
   *
   * `definitions` is only ever the catalog rows for THIS delivery's leftovers,
   * never a whole panel; see definitionsForOffer. Placement then runs over every
   * stored row against the whole library, not just the new definitions, so a
   * marker the practitioner added by hand last week picks up its waiting value
   * on the same click.
   */
  async addImportedExtrasToMyMarkers(
    bloodwork: Bloodwork,
    definitions: CustomMarkerDefinition[],
  ): Promise<Bloodwork> {
    const profile = this.userProfileUIStore?.currentUserProfile;
    if (!profile) {
      throw new Error('No practitioner profile is loaded, so My Markers cannot be saved.');
    }

    if (definitions.length) {
      this.userProfileUIStore.updateCustomMarkerLibrary([
        ...(profile.customMarkerLibrary || []),
        ...definitions,
      ]);
      await this.userProfileUIStore.saveCurrentUserProfile();
    }

    const library = this.userProfileUIStore.currentUserProfile?.customMarkerLibrary || [];
    const { placed, unplaced } = placeRowsOntoTest(bloodwork.importedExtras || [], library);

    // Nothing placed and nothing to add is not an error, it is a no-op: the
    // button should not have been offered, and saving the test anyway would
    // bump its updatedAt for no reason.
    if (!placed.length) {
      return bloodwork;
    }

    // Appended, not replaced. The practitioner may have typed their own markers
    // onto this test, and a delivered row filling in must not remove them.
    // Keyed the way the library is, so a name already on the test is overwritten
    // in place rather than added a second time.
    const byKey = new Map<string, CustomMarker>();
    (bloodwork.customMarkers || []).forEach(marker => {
      byKey.set(CustomMarker.keyFor(marker.name || ''), marker);
    });
    placed.forEach(marker => byKey.set(CustomMarker.keyFor(marker.name || ''), marker));
    // Array.from rather than a spread: this project targets a build without
    // downlevelIteration, so spreading a Map iterator does not compile.
    bloodwork.customMarkers = Array.from(byKey.values());

    // What is still waiting stays waiting, so the notice can keep asking. An
    // empty list is stored as undefined rather than [], which is what the
    // model's own guard reads as "nothing waiting".
    bloodwork.importedExtras = unplaced.length ? unplaced : undefined;

    return this.saveBloodwork(bloodwork);
  }

  saveBloodwork(
    bloodwork: Bloodwork,
    options: { silent?: boolean } = {},
  ): Promise<Bloodwork> {
    const silent = !!options.silent;
    if (!silent) this.loadingStore.appIsBusy();
    const wasNew = bloodwork.isNew();
    return this.bloodworkStore.saveBloodwork(bloodwork).then((savedBloodwork) => {
      if (wasNew) {
        this.userProfileUIStore.currentUserProfile.incrementNumTests();
        this.userProfileUIStore.saveCurrentUserProfile();
      }
      this.refreshSavedBloodwork(savedBloodwork);
      if (!silent) this.loadingStore.appIsNotBusy();
      return savedBloodwork;
    }, (error: any) => {
      console.error(error);
      if (!silent) this.loadingStore.appIsNotBusy();
      throw error;
    });
  }

  @action deleteBloodwork(bloodwork: Bloodwork): Promise<any> {
    this.loadingStore.appIsBusy();
    return this.bloodworkStore.deleteBloodwork(bloodwork).then((result) => {
      return this.refreshBloodworks();
    }).then(() => {
      this.loadingStore.appIsNotBusy();
    }).catch((error) => {
      console.error(error);
      this.loadingStore.appIsNotBusy();
    });
  }

  @action deleteClientBloodworks(clientId: string): Promise<any> {
    if (this.clientBloodworks.length === 0) {
      return Promise.resolve();
    }

    this.loadingStore.appIsBusy();
    const promises = [];
    this.clientBloodworks.forEach((bloodwork) => {
      promises.push(this.bloodworkStore.deleteBloodwork(bloodwork));
    });
    return Promise.all(promises).then(() => {
      return this.loadClientBloodworks(clientId);
    }, (error) => {
      console.error(error);
      return this.loadClientBloodworks(clientId);
    }).catch((error) => {
      console.error(error);
      return this.loadClientBloodworks(clientId);
    });
  }
}